import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة
import { Plus, Edit3, Trash2, Settings, Zap, Download, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFontSize } from './FontSizeContext';

const WorkflowList = ({ onEdit }) => {
  const { t, i18n } = useTranslation();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();   
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  // 1. دالة جلب البيانات من الباكيند
  const fetchWorkflows = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/workflow/processes', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWorkflows(res.data);
    } catch (err) {
      toast.error(t('workflow.toasts.fetch_fail'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkflows();
  }, []);

  // 2. دالة الاكسبورت (CSV Export)
  const exportToCSV = () => {
    if (workflows.length === 0) return toast.error(t('workflow.toasts.no_data'));
    
    // العناوين مترجمة في ملف الإكسل
    const headers = `${t('workflow.table.id')},${t('workflow.table.name')},${t('workflow.table.app')},${t('workflow.table.status')},${t('workflow.table.created')}\n`;
    const rows = workflows.map(wf => 
      `${wf.id},${wf.process_name},${wf.object_name},${wf.is_active ? t('common.active') : t('common.inactive')},${wf.created_at || ''}`
    ).join("\n");

    const blob = new Blob([headers + rows], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `workflows_${new Date().toLocaleDateString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={`p-6 bg-surface-panel h-screen overflow-y-auto ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }}>
      {/* Header */}
      <div className={`flex justify-between items-center mb-8 bg-surface-card p-4 rounded-xl shadow-sm border border-line-subtle`}>
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <h1 className={`text-2xl font-black text-primary-dark uppercase tracking-tighter flex items-center gap-2 `}>
            <Zap className="text-primary" /> {t('workflow.title')}
          </h1>
          <p className="text-ink-secondary text-[10px] font-bold uppercase tracking-widest">{t('workflow.subtitle')}</p>
        </div>
        <div className={`flex gap-3`}>
          <button onClick={exportToCSV} className="border border-line text-ink px-4 py-2 rounded-lg font-black text-[1.0em] flex items-center gap-2 hover:bg-surface-panel transition-all uppercase">
            <Download size={14}/> {t('workflow.export_btn')}
          </button>
          <button onClick={() => navigate('/workflow/new')} className="bg-primary-dark text-white px-6 py-2 rounded-lg shadow-lg font-black text-[1.0em] flex items-center gap-2 hover:bg-black transition-all uppercase">
            <Plus size={16}/> {t('workflow.new_process')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center h-64 opacity-50">
          <RefreshCw className="animate-spin mb-2" size={32} />
          <span className="font-black text-[1.0em] uppercase">{t('workflow.loading')}</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in duration-500">
          {workflows.map(wf => (
            <div key={wf.id} className="bg-surface-card border border-line-subtle rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden">
               <div className={`absolute top-0 ${isRTL ? 'left-0 -ml-8' : 'right-0 -mr-8'} -mt-8 w-24 h-24 rounded-full opacity-5 ${wf.is_active ? 'bg-status-success' : 'bg-status-danger'}`}></div>

              <div className={`flex justify-between items-start mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`p-3 rounded-xl ${wf.is_active ? 'bg-status-success-bg text-status-success' : 'bg-status-danger-bg text-status-danger'}`}>
                  <Zap size={24} fill="currentColor"/>
                </div>
                <div className={`flex flex-col ${isRTL ? 'items-start' : 'items-end'}`}>
                  <span className={`text-[0.9em] font-black px-3 py-1 rounded-full ${wf.is_active ? 'bg-status-success-bg text-status-success' : 'bg-status-danger-bg text-status-danger'}`}>
                    {wf.is_active ? t('common.active') : t('common.inactive')}
                  </span>
                  <span className="text-[0.8em] text-ink-secondary mt-1 font-mono">v.{wf.version || 1.0}</span>
                </div>
              </div>

              <h3 className="font-black text-primary-dark text-xl mb-1 group-hover:text-primary transition-colors uppercase truncate">
                {wf.process_name || t('workflow.unnamed')}
              </h3>
              <p className="text-ink text-xs mb-4 line-clamp-2 italic h-8">
                {wf.description || t('workflow.no_desc')}
              </p>
              <p className={`text-ink-secondary text-[1.0em] font-bold uppercase mb-6 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {t('workflow.linked_to')} <span className="text-status-warning bg-status-warning-bg px-2 py-0.5 rounded">{wf.object_name}</span>
              </p>

              <div className="flex gap-2 border-t border-line-subtle pt-5">
                <button onClick={() => navigate(`/workflow/${wf.id}`)} className="flex-1 flex justify-center py-2.5 hover:bg-primary-soft rounded-xl text-ink-secondary hover:text-primary border border-transparent hover:border-primary-soft transition-all" title={t('workflow.tooltips.edit')}>
                  <Edit3 size={18}/>
                </button>
                <button className="flex-1 flex justify-center py-2.5 hover:bg-surface-panel rounded-xl text-ink-secondary hover:text-ink border border-transparent hover:border-line transition-all" title={t('workflow.tooltips.settings')}>
                  <Settings size={18}/>
                </button>
                <button className="flex-1 flex justify-center py-2.5 hover:bg-status-danger-bg rounded-xl text-ink-secondary hover:text-status-danger border border-transparent hover:border-status-danger-bg transition-all" title={t('workflow.tooltips.delete')}>
                  <Trash2 size={18}/>
                </button>
              </div>
            </div>
          ))}

          {/* Quick Add Card */}
          <div onClick={() => navigate('/workflow/new')} className="border-2 border-dashed border-line rounded-2xl p-6 flex flex-col items-center justify-center text-ink-secondary hover:border-primary hover:text-primary cursor-pointer transition-all hover:bg-primary-soft min-h-[250px]">
             <Plus size={48} strokeWidth={1} />
             <span className="font-black text-[10px] uppercase mt-2 tracking-widest">{t('workflow.create_path')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowList;